<script>
import Layout from "../../layouts/table";
import {mapGetters} from "vuex";
import moment from "moment";

export default {
    components: {
        Layout,
    },

    computed: {
        ...mapGetters({
            items: 'sms/out',
            pager: 'sms/outPager',
            itemsLoading: 'sms/loading'
        }),
        rows: function () {
            return this.items && this.items.length
        },
        fields: function () {
            return [
                {
                    key: "type",
                    label: this.$t('sms.type'),
                    thClass: 'gridjs-th',
                    formatter: (value, key, item) => !item.list_id ? this.$t('sms.to_target') : this.$t('sms.to_list'),
                    tdClass: 'no-wrap',
                    sortable: true
                },
                {
                    key: "number",
                    label: this.$t('sms.phone'),
                    thClass: 'gridjs-th',
                    tdClass: 'no-wrap',
                    formatter: (value) => value.phone(),
                    sortable: true
                },
                {
                    key: "status",
                    label: this.$t('sms.status'),
                    tdClass: 'no-wrap',
                    thClass: 'gridjs-th border-left-0',
                    sortable: true
                },
                {
                    key: "message",
                    label: this.$t('sms.message'),
                    thClass: 'gridjs-th border-left-0',
                    formatter: (value) => value?.truncate(50),
                    sortable: true
                },
                {
                    key: "create_date",
                    label: this.$t('sms.date'),
                    thClass: 'gridjs-th',
                    formatter: (value) => {
                        return moment(value).format('YYYY. MM. DD. HH:mm')
                    },
                    sortable: true
                },
                {
                    key: "view",
                    label: this.$t('sms.view_stats'),
                    thClass: 'gridjs-th',
                    tdClass: 'text-start'
                },
                {
                    key: "price",
                    label: this.$t('sms.net_price'),
                    thClass: 'gridjs-th',
                    tdClass: 'text-end',
                    formatter: (value) => this.formatter.format(value),
                    sortable: true
                },
            ];
        },
    },
    data() {
        return {
            page: (this.pager && this.pager.current_page) || 1,
            perPage: (this.pager && this.pager.limit) || 10,
            currentPage: (this.pager && this.pager.current_page) || 1,
            sortBy: (this.pager && this.pager.column) || 'create_date',
            sortDesc: (this.pager && this.pager.desc) || true,
            formatter: new Intl.NumberFormat('hu-HU', {
                style: 'currency',
                currency: 'HUF',
                maximumFractionDigits: 1
            })
        };
    },
    methods: {
        toPage: function (val) {
            this.page = val
            this.delayedFetch()
        },
        delayedFetch: function () {
            this.$store.dispatch('sms/fetchOutDelayed', {
                limit: this.perPage,
                page: this.page,
                sorting: {
                    column: this.sortBy,
                    desc: this.sortDesc
                }
            });
        },
        rowColor: function (item) {
            if (item.sms_status === 'CANCELLED' || (item.list_status === 'CANCELLED' && item.sms_status === 'WAITING')) {
                return 'bg-danger-subtle';
            }

            if (item.sms_status === 'SENT' || item.sms_status === 'OK') {
                return 'bg-success-subtle';
            }

            if (item.sms_status === 'EXP') {
                return 'bg-danger-subtle';
            }

            if (item.sms_status === 'UNDEL') {
                return 'bg-danger-subtle';
            }

            if (item.sms_status === 'ERROR') {
                return 'bg-danger-subtle';
            }
        },
    },
    watch: {
        sortBy: function () {
            this.delayedFetch();
        },
        sortDesc: function () {
            this.delayedFetch();
        },
    },
    created() {
        document.title = this.setTitle(this.$t('sms.sms_sent'));
        this.delayedFetch();
    }
};
</script>

<template>
    <Layout>
        <div class="card">
            <div class="card-header">
                <h4 class="card-title">{{ $t('sms.sms_sent') }}</h4>
            </div>
            <div class="card-body p-0 pb-3 p-md-3">
                <!-- Table -->
                <div class="table-responsive mh-70-vh mh-md-auto">
                    <div class="mb-0">
                        <b-table
                            :items="items"
                            :fields="fields"
                            responsive="sm"
                            :sort-by.sync="sortBy"
                            :sort-desc.sync="sortDesc"
                            :busy="itemsLoading"
                            hover
                            head-variant="light"
                            no-local-sorting
                            :tbody-tr-class="rowColor"
                        >
                            <template #table-busy>
                                <div class="d-flex justify-content-center">
                                    <b-spinner></b-spinner>
                                </div>
                            </template>
                            <template #cell(type)="scope">
                                <i class="me-1 fas" :class="{'fa-users text-dark': scope.item.list_id, 'fa-mobile text-success': !scope.item.list_id}"></i>
                                {{ scope.value }}
                            </template>
                            <template #cell(view)="scope">
                                <template v-if="scope.item.sum_view">
                                    <span class="badge bg-primary cursor-pointer" :title="$t('sms.views')">
                                        <i class="fas fa-eye me-1"></i>
                                        <strong>{{ scope.item.sum_view }}</strong>
                                    </span>
                                    <span class="mx-2">|</span>
                                    <span class="badge bg-info cursor-pointer" :title="$t('sms.viewers')">
                                        <i class="fas fa-user me-1"></i>
                                    <span>{{ scope.item.unique_view }}</span>
                                    </span>
                                </template>
                            </template>
                            <template #cell(status)="scope">
                                <template>
                                    <i v-if="scope.item.sms_status === 'CANCELLED' || (scope.item.list_status === 'CANCELLED' && scope.item.sms_status === 'WAITING')" class="fas fa-times-circle text-danger"></i>
                                    <i v-else-if="scope.item.sms_status === 'WAITING'" class="fas fa-hourglass-half"></i>
                                    <i v-else-if="scope.item.sms_status === 'SENT' || scope.item.sms_status === 'OK'" class="fas fa-check-circle text-success"></i>
                                    <i v-else-if="scope.item.sms_status === 'EXP'" class="fas fa-exclamation-circle text-danger"></i>
                                    <i v-else-if="scope.item.sms_status === 'WAIT'" class="fas fa-hourglass-end text-warning"></i>
                                    <i v-else-if="scope.item.sms_status === 'UNDEL'" class="fas fa-exclamation-triangle text-danger"></i>
                                    <i v-else-if="scope.item.sms_status === 'ERROR'" class="fas fa-exclamation-triangle text-danger"></i>
                                    <i v-else-if="scope.item.sms_status === 'NOSTATUS'" class="fas fa-question-circle text-secondary"></i>
                                    <span class="ms-2">{{ scope.value }}</span>
                                </template>
                            </template>
                        </b-table>
                    </div>
                </div>
                <div class="row">
                    <div class="col">
                        <div class="d-flex w-100 align-items-center justify-content-center justify-content-md-end">
                            <div
                                class="dataTables_paginate paging_simple_numbers"
                            >
                                <ul class="pagination pagination-rounded mb-0">
                                    <!-- pagination -->
                                    <b-pagination
                                        v-model="page"
                                        :total-rows="pager.count"
                                        :per-page="perPage"
                                        @change="toPage"
                                    ></b-pagination>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>
